import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';

/**
 * Booking progress (design-spec §3 "Stepper").
 *
 * Desktop (md+): 26px circles + 14px labels joined by 34×1 line-strong connectors.
 * - done     — success circle with a white check; label-2 500
 * - active   — accent circle with white 13/700 number; ink 700 label; `aria-current="step"`
 * - upcoming — 1px line-strong outline, label-2 number and label
 *
 * Mobile (<md): the design swaps it for a "Step 2 of 3" subtitle.
 */
export interface StepperProps {
  steps: string[];
  /** 0-based index of the active step. Steps before it are done. */
  current: number;
  className?: string;
}

export function Stepper({ steps, current, className }: StepperProps) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t('ui.stepper.label')} className={className}>
      <ol className="hidden items-center gap-[18px] md:flex">
        {steps.map((label, i) => {
          const state =
            i < current ? 'done' : i === current ? 'active' : 'upcoming';
          return (
            <li
              key={label}
              className="flex items-center gap-[9px] font-sans text-[14px]"
              aria-current={state === 'active' ? 'step' : undefined}
              data-state={state}
            >
              {i > 0 && (
                <span
                  aria-hidden="true"
                  className="mr-[9px] h-px w-[34px] bg-sl-line-strong"
                />
              )}
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-[26px] w-[26px] items-center justify-center rounded-full text-[13px] tabular-nums',
                  state === 'done' && 'bg-sl-success text-white',
                  state === 'active' && 'bg-sl-accent font-bold text-white',
                  state === 'upcoming' &&
                    'border border-sl-line-strong font-semibold text-sl-label-2'
                )}
              >
                {state === 'done' ? (
                  <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
                ) : (
                  i + 1
                )}
              </span>
              <span
                className={cn(
                  state === 'active'
                    ? 'font-bold text-sl-ink'
                    : 'font-medium text-sl-label-2'
                )}
              >
                {label}
                {state === 'done' && (
                  <span className="sr-only"> ({t('ui.stepper.done')})</span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
      <p className="font-sans text-[12px] text-sl-muted md:hidden">
        {t('ui.stepper.stepOf', { current: current + 1, total: steps.length })}
      </p>
    </nav>
  );
}
